import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

/**
 * ProgressChart Component
 * Visualizes sales target progress with various chart types
 */
const ProgressChart = ({ data, type = 'line', title, height = 300 }) => {
  const COLORS = {
    primary: '#3B82F6',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    purple: '#8B5CF6',
    teal: '#14B8A6'
  };

  const PIE_COLORS = [COLORS.primary, COLORS.success, COLORS.warning, COLORS.danger, COLORS.purple, COLORS.teal];

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-xs text-gray-600">
              <span style={{ color: entry.color }}>{entry.name}:</span>{' '}
              <span className="font-medium">{entry.value.toLocaleString()}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Render Line Chart
  const renderLineChart = () => (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
        <XAxis 
          dataKey="date" 
          tick={{ fontSize: 12 }}
          stroke="#9CA3AF"
        />
        <YAxis 
          tick={{ fontSize: 12 }}
          stroke="#9CA3AF"
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          wrapperStyle={{ fontSize: '12px' }}
          iconType="line"
        />
        <Line 
          type="monotone" 
          dataKey="target" 
          stroke={COLORS.danger} 
          strokeWidth={2}
          strokeDasharray="5 5"
          name="Target"
          dot={false}
        />
        <Line 
          type="monotone" 
          dataKey="actual" 
          stroke={COLORS.primary} 
          strokeWidth={2}
          name="Actual"
          dot={{ fill: COLORS.primary, r: 4 }}
          activeDot={{ r: 6 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  // Render Bar Chart
  const renderBarChart = () => (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
        <XAxis 
          dataKey="name" 
          tick={{ fontSize: 12 }}
          stroke="#9CA3AF"
        />
        <YAxis 
          tick={{ fontSize: 12 }}
          stroke="#9CA3AF"
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          wrapperStyle={{ fontSize: '12px' }}
        />
        <Bar 
          dataKey="target" 
          fill={COLORS.warning} 
          name="Target"
          radius={[4, 4, 0, 0]}
        />
        <Bar 
          dataKey="actual" 
          fill={COLORS.success} 
          name="Actual"
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );

  // Render Pie Chart
  const renderPieChart = () => {
    const RADIAN = Math.PI / 180;
    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
      const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
      const x = cx + radius * Math.cos(-midAngle * RADIAN);
      const y = cy + radius * Math.sin(-midAngle * RADIAN);

      return (
        <text
          x={x}
          y={y}
          fill="white"
          textAnchor={x > cx ? 'start' : 'end'}
          dominantBaseline="central"
          fontSize="12"
          fontWeight="600"
        >
          {`${(percent * 100).toFixed(0)}%`}
        </text>
      );
    };

    return (
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend 
            wrapperStyle={{ fontSize: '12px' }}
            verticalAlign="bottom"
            height={36}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  // Render Progress Bars
  const renderProgressBars = () => (
    <div className="space-y-4">
      {data.map((item, index) => {
        const percentage = item.target > 0 ? (item.actual / item.target) * 100 : 0;
        const status = percentage >= 100 ? 'success' : percentage >= 80 ? 'primary' : percentage >= 50 ? 'warning' : 'danger';
        const barColor = COLORS[status];

        return (
          <div key={index} className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700">{item.name}</span>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-500">
                  {item.actual.toLocaleString()} / {item.target.toLocaleString()}
                </span>
                <span className="text-sm font-semibold" style={{ color: barColor }}>
                  {percentage.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="h-3 rounded-full transition-all duration-500 ease-out"
                style={{
                  width: `${Math.min(percentage, 100)}%`,
                  backgroundColor: barColor
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      )}
      
      {type === 'line' && renderLineChart()}
      {type === 'bar' && renderBarChart()}
      {type === 'pie' && renderPieChart()}
      {type === 'progress' && renderProgressBars()}

      {(!data || data.length === 0) && (
        <div className="flex items-center justify-center h-64 text-gray-400">
          <div className="text-center">
            <p className="text-sm">No data available</p>
            <p className="text-xs mt-1">Progress data will appear here once metrics are calculated</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressChart;


